// Validación de claves GS1 (checklist de certificación GS1, sección 1).
// El dígito verificador GS1 es el mismo algoritmo mod-10 para GTIN-8/12/13/14,
// GLN (13), SSCC (18), GSIN (17) y GSRN (18): pesos 3/1 alternados desde la derecha.

/**
 * Calcula el dígito verificador GS1 (mod-10) para el cuerpo de una clave
 * (todos los dígitos MENOS el verificador).
 */
export function gs1CheckDigit(body) {
  const digits = String(body).replace(/\D/g, '')
  let sum = 0
  // Peso 3 en la posición inmediatamente a la izquierda del verificador, alternando
  for (let i = digits.length - 1, w = 3; i >= 0; i--, w = 4 - w) {
    sum += Number(digits[i]) * w
  }
  return (10 - (sum % 10)) % 10
}

/**
 * Valida una clave GS1 completa (con su dígito verificador al final).
 * Acepta GTIN-8/12/13/14, GLN (13), SSCC (18), GSIN (17), GSRN (18).
 */
export function isValidGS1Key(key, validLengths) {
  const s = String(key || '').trim()
  if (!/^\d+$/.test(s)) return false
  if (validLengths && !validLengths.includes(s.length)) return false
  return gs1CheckDigit(s.slice(0, -1)) === Number(s[s.length - 1])
}

export const GTIN_LENGTHS = [8, 12, 13, 14]

/**
 * Un GS1-128/DataMatrix que solo trae (01) llega del lector como "01"+GTIN-14
 * (16 caracteres, sin FNC1 ni AIM cuando el lector no los transmite). Se
 * distingue de un código interno de 16 dígitos por el dígito verificador.
 */
export function isAI01Only(code) {
  const s = String(code || '').trim()
  return /^01\d{14}$/.test(s) && isValidGS1Key(s.slice(2), [14])
}

/**
 * Valida el campo "Código de Barras / GTIN" de un producto.
 * Política GS1 (1ª evaluación, puntos 1.1-1.4): el GTIN es SIEMPRE numérico,
 * con longitud 8/12/13/14 y dígito verificador correcto. Los códigos internos
 * alfanuméricos van en SKU o Código de Fábrica, no en este campo.
 * Devuelve { valid, isGS1, message }.
 */
export function validateProductBarcode(code) {
  const s = String(code || '').trim()
  if (!s) return { valid: false, isGS1: false, message: 'Código de Barras es requerido' }
  if (!/^\d+$/.test(s)) {
    return { valid: false, isGS1: false, message: 'El GTIN debe ser solo numérico (sin letras). Los códigos internos van en SKU o Código de Fábrica.' }
  }
  if (!GTIN_LENGTHS.includes(s.length)) {
    return { valid: false, isGS1: false, message: `Largo inválido (${s.length} dígitos): un GTIN tiene 8, 12, 13 o 14 dígitos` }
  }
  if (!isValidGS1Key(s, GTIN_LENGTHS)) {
    // Un UPC-E de 8 dígitos usa el verificador de su UPC-A expandido (4 formas
    // de expansión según el 7° dígito), NO el de EAN-8
    if (s.length === 8) {
      const upca = expandUPCE(s)
      if (upca) {
        return { valid: true, isGS1: true, message: null, upcaExpansion: upca }
      }
      const expected = gs1CheckDigit(s.slice(0, -1))
      return {
        valid: false,
        isGS1: true,
        message: `8 dígitos: no es EAN-8 válido (verificador sería ${expected}) ni UPC-E válido ` +
                 '(debe comenzar con 0 o 1 y su verificador se calcula sobre el UPC-A expandido)'
      }
    }
    const expected = gs1CheckDigit(s.slice(0, -1))
    return {
      valid: false,
      isGS1: true,
      message: `Dígito verificador GS1 inválido para GTIN-${s.length} (esperado ${expected}, recibido ${s[s.length - 1]})`
    }
  }
  // EAN-8 válido de 8 dígitos: se acepta como EAN-8 (si además calza como
  // UPC-E es ambiguo sin AIM — prevalece EAN-8, documentado para el auditor)
  return { valid: true, isGS1: true, message: null }
}

/**
 * Expande un UPC-E (8 dígitos: number system 0/1 + 6 de cuerpo + verificador)
 * a su UPC-A (12 dígitos) equivalente. Devuelve null si no es un UPC-E válido.
 */
export function expandUPCE(code) {
  const s = String(code || '').trim()
  if (!/^[01]\d{7}$/.test(s)) return null
  const ns = s[0]
  const d = s.slice(1, 7) // 6 dígitos de cuerpo
  const check = s[7]
  const last = d[5]
  let body11 // 11 dígitos de UPC-A sin verificador
  if (last === '0' || last === '1' || last === '2') {
    body11 = ns + d.slice(0, 2) + last + '0000' + d.slice(2, 5)
  } else if (last === '3') {
    body11 = ns + d.slice(0, 3) + '00000' + d.slice(3, 5)
  } else if (last === '4') {
    body11 = ns + d.slice(0, 4) + '00000' + d[4]
  } else {
    body11 = ns + d.slice(0, 5) + '0000' + last
  }
  const upca = body11 + check
  // El verificador del UPC-E es el del UPC-A expandido: si no calza, no era UPC-E
  if (gs1CheckDigit(body11) !== Number(check)) return null
  return upca
}
